import React, { useRef } from "react";
import { Bold, Italic, Link as LinkIcon } from "lucide-react";

/**
 * Textarea (or input) with a small toolbar that inserts minimal markdown at
 * the current cursor position: **bold**, *italic*, [text](url).
 * Keeps state controlled by the parent via value/onChange.
 */
export default function RichTextField({
  value,
  onChange,
  rows = 3,
  placeholder,
  singleLine = false,
  testId,
}) {
  const ref = useRef(null);

  const wrap = (before, after = before, promptForUrl = false) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const selected = value.slice(start, end);
    let inserted;
    if (promptForUrl) {
      const label = selected || "text";
      const url = window.prompt("Link URL (https://…, mailto:… or /path):", "https://");
      if (!url) return;
      inserted = `[${label}](${url})`;
    } else {
      inserted = `${before}${selected || "text"}${after}`;
    }
    const next = value.slice(0, start) + inserted + value.slice(end);
    onChange(next);
    // Restore cursor around the inserted text
    requestAnimationFrame(() => {
      el.focus();
      const cursorPos = start + inserted.length;
      el.setSelectionRange(cursorPos, cursorPos);
    });
  };

  const btnCls = "inline-flex items-center justify-center w-7 h-7 rounded text-brand-ink/70 hover:bg-brand-paper hover:text-brand-blue transition-colors";

  return (
    <div>
      <div className="flex items-center gap-1 mb-1.5" data-testid={testId ? `${testId}-toolbar` : undefined}>
        <button type="button" title="Bold (Ctrl+B)" onClick={() => wrap("**")} className={btnCls} data-testid={testId ? `${testId}-bold` : undefined}>
          <Bold size={13} />
        </button>
        <button type="button" title="Italic (Ctrl+I)" onClick={() => wrap("*")} className={btnCls} data-testid={testId ? `${testId}-italic` : undefined}>
          <Italic size={13} />
        </button>
        <button type="button" title="Insert link" onClick={() => wrap("", "", true)} className={btnCls} data-testid={testId ? `${testId}-link` : undefined}>
          <LinkIcon size={13} />
        </button>
        <span className="ml-2 text-[11px] text-brand-ink/40">
          <code className="text-[10.5px]">**bold**</code> · <code className="text-[10.5px]">*italic*</code> · <code className="text-[10.5px]">[link](url)</code>
        </span>
      </div>
      {singleLine ? (
        <input
          ref={ref}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") { e.preventDefault(); wrap("**"); }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "i") { e.preventDefault(); wrap("*"); }
          }}
          placeholder={placeholder}
          data-testid={testId}
          className="w-full rounded-md border border-brand-rule bg-white/80 px-4 py-2.5 text-[15px] focus:outline-none focus:border-brand-blue"
        />
      ) : (
        <textarea
          ref={ref}
          rows={rows}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") { e.preventDefault(); wrap("**"); }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "i") { e.preventDefault(); wrap("*"); }
          }}
          placeholder={placeholder}
          data-testid={testId}
          className="w-full rounded-md border border-brand-rule bg-white/80 px-4 py-2.5 text-[15px] focus:outline-none focus:border-brand-blue resize-y"
        />
      )}
    </div>
  );
}
